import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { roomService } from 'api/services/roomService';
import { propertyService } from 'api/services/propertyService';
import { roomCategoryService } from 'api/services/roomCategoryService';
import type { Room, CreateRoomRequest, UpdateRoomRequest, Property, RoomCategory } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface RoomModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Room | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, roomId?: string) => void;
}

interface RoomFormData {
  propertyId: string;
  roomCategoryId: string;
  roomNumber: string;
  floorNumber: string;
  roomStatus: string;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: RoomFormData = {
  propertyId: '',
  roomCategoryId: '',
  roomNumber: '',
  floorNumber: '',
  roomStatus: 'Available',
  isActive: true,
};

const roomStatusOptions = [
  { value: 'Available', label: 'Available' },
  { value: 'Occupied', label: 'Occupied' },
  { value: 'Dirty', label: 'Dirty' },
  { value: 'OOO', label: 'Out of Order' },
  { value: 'Blocked', label: 'Blocked' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const RoomModal: React.FC<RoomModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<RoomFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Dropdown data ---
  const [properties, setProperties] = useState<Property[]>([]);
  const [roomCategories, setRoomCategories] = useState<RoomCategory[]>([]);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<Room | null | undefined>(record);

  // --- Load dropdown data ---
  useEffect(() => {
    if (!isOpen) return;
    const loadDropdowns = async () => {
      try {
        const [propRes, catRes] = await Promise.all([
          propertyService.getAll({ pageSize: 500, includeInactive: false }),
          roomCategoryService.getAll({ pageSize: 500, includeInactive: false }),
        ]);
        setProperties(propRes.data || []);
        setRoomCategories(catRes.data || []);
      } catch {
        // Silently ignore
      }
    };
    loadDropdowns();
  }, [isOpen]);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: RoomFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        propertyId: rec.propertyId || '',
        roomCategoryId: rec.roomCategoryId || '',
        roomNumber: mode === 'copy' ? '' : (rec.roomNumber || ''),
        floorNumber: rec.floorNumber || '',
        roomStatus: rec.roomStatus || 'Available',
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Filter room categories by selected property ---
  const filteredRoomCategories = useMemo(() => {
    if (!formData.propertyId) return roomCategories;
    return roomCategories.filter(cat => !cat.propertyId || cat.propertyId === formData.propertyId);
  }, [roomCategories, formData.propertyId]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const num = currentFormData.roomNumber?.trim();
      const idx = currentCreateInstanceId || '1';
      title = num ? `Add - ${num} (${idx})` : `Add Room (${idx})`;
    } else if (currentMode === 'copy') {
      const num = currentFormData.roomNumber?.trim();
      const idx = currentCopyInstanceId || '1';
      title = num ? `Copy - ${num} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.roomNumber || 'Room'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? (recordRef.current?.id as string) : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.propertyId) {
      errors.propertyId = 'Property is required';
    }

    if (!formData.roomCategoryId) {
      errors.roomCategoryId = 'Room Category is required';
    }

    if (!formData.roomNumber.trim()) {
      errors.roomNumber = 'Room Number is required';
    } else if (formData.roomNumber.length > 20) {
      errors.roomNumber = 'Room Number cannot exceed 20 characters';
    }

    if (formData.floorNumber && formData.floorNumber.length > 10) {
      errors.floorNumber = 'Floor Number cannot exceed 10 characters';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async code uniqueness validation for create/copy modes
    if (mode !== 'edit') {
      try {
        const response = await roomService.validateNumber(formData.roomNumber.trim(), formData.propertyId || undefined);
        if (!response.data) {
          setFieldErrors({ roomNumber: 'This room number already exists' });
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="roomNumber"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.querySelector<HTMLElement>('input')?.focus();
            }
          });
          return;
        }
      } catch {
        // If validation endpoint fails, proceed with save
      }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateRoomRequest | UpdateRoomRequest = {
        propertyId: formData.propertyId || undefined,
        roomCategoryId: formData.roomCategoryId || undefined,
        roomNumber: formData.roomNumber.trim(),
        floorNumber: formData.floorNumber || undefined,
        roomStatus: formData.roomStatus || undefined,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await roomService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateRoomRequest);
      } else {
        await roomService.create(payload as CreateRoomRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `Room "${formData.roomNumber}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await roomService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: RoomFormData = {
          propertyId: rec.propertyId || '',
          roomCategoryId: rec.roomCategoryId || '',
          roomNumber: rec.roomNumber || '',
          floorNumber: rec.floorNumber || '',
          roomStatus: rec.roomStatus || 'Available',
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // --- Room number validation on blur ---
  const handleNumberBlur = useCallback(async () => {
    const num = formData.roomNumber.trim();
    if (!num) return;
    if (!formData.propertyId) return; // skip validation until property is selected
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await roomService.validateNumber(num, propertyId, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, roomNumber: 'This room number already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.roomNumber, formData.propertyId, mode, record]);

  if (!isOpen) return null;

  const propertyOptions = properties.map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  const roomCategoryOptions = filteredRoomCategories.map(c => ({ value: c.id, label: `${c.categoryCode} - ${c.categoryName}` }));

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Room' : mode === 'copy' ? 'Copy Room' : 'Edit Room'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Room Number & Floor --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="roomNumber">
              <label className="form-label">Room Number <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('roomNumber') ? ' form-field-error' : ''}`}
                value={formData.roomNumber}
                onChange={(e) => { clearFieldError('roomNumber'); setFormData(prev => ({ ...prev, roomNumber: e.target.value })); }}
                onBlur={handleNumberBlur}
                placeholder="e.g., 101"
                maxLength={20}
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : undefined}
                autoFocus={mode === 'create' || mode === 'copy'}
              />
              {getFieldError('roomNumber') && <div className="form-error-message">{getFieldError('roomNumber')}</div>}
            </div>

            <div className="form-group" data-field="floorNumber">
              <label className="form-label">Floor Number</label>
              <input
                type="text"
                className={`form-input${getFieldError('floorNumber') ? ' form-field-error' : ''}`}
                value={formData.floorNumber}
                onChange={(e) => { clearFieldError('floorNumber'); setFormData(prev => ({ ...prev, floorNumber: e.target.value })); }}
                placeholder="e.g., 1"
                maxLength={10}
              />
              {getFieldError('floorNumber') && <div className="form-error-message">{getFieldError('floorNumber')}</div>}
            </div>
          </div>

          {/* --- Property & Room Category --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label="Property"
                required
                options={propertyOptions}
                value={formData.propertyId}
                onChange={(val) => {
                  clearFieldError('propertyId');
                  setFormData(prev => ({ ...prev, propertyId: (val as string) || '', roomCategoryId: '' }));
                }}
                placeholder="Select property..."
                isClearable
                size="md"
                error={getFieldError('propertyId')}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="form-group" data-field="roomCategoryId">
              <SyutSelect
                label="Room Category"
                required
                options={roomCategoryOptions}
                value={formData.roomCategoryId}
                onChange={(val) => {
                  clearFieldError('roomCategoryId');
                  setFormData(prev => ({ ...prev, roomCategoryId: (val as string) || '' }));
                }}
                placeholder="Select category..."
                isClearable
                size="md"
                error={getFieldError('roomCategoryId')}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          {/* --- Status & Active --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="roomStatus">
              <SyutSelect
                label="Room Status"
                options={roomStatusOptions}
                value={formData.roomStatus}
                onChange={(val) => setFormData(prev => ({ ...prev, roomStatus: (val as string) || 'Available' }))}
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Room'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} room "${formData.roomNumber}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default RoomModal;
